/**
 * CIS (Construction Industry Scheme) Management Page
 *
 * Full-featured CIS management with four tabs:
 *  1. Dashboard — overview stats, next return due, verification summary
 *  2. Subcontractors — registry with CRUD, HMRC verification
 *  3. Deductions — record & view CIS deductions with calculator
 *  4. Returns & Statements — CIS300 generation/submission, payment statements
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Paper, Grid, Card, CardContent,
  Button, IconButton, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Divider, MenuItem, Select, FormControl, InputLabel,
  Tooltip, LinearProgress, Stack, InputAdornment, FormControlLabel, Checkbox,
} from '@mui/material';
import {
  Construction as CISIcon,
  People as SubIcon,
  Receipt as DeductionIcon,
  Description as ReturnIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  VerifiedUser as VerifyIcon,
  Calculate as CalcIcon,
  Send as SubmitIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Mail as MailIcon,
} from '@mui/icons-material';
import { apiClient, getErrorMessage } from '../api/client';

// ============================================================================
// Types
// ============================================================================

interface Subcontractor {
  id: number;
  business_name: string;
  trading_name?: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  utr: string;
  ni_number?: string;
  company_registration?: string;
  vat_number?: string;
  address_line_1?: string;
  town_city?: string;
  postcode?: string;
  verification_status: string;
  verification_date?: string;
  deduction_rate: number;
  is_sole_trader: boolean;
  is_partnership: boolean;
  is_company: boolean;
  is_active: boolean;
}

interface CISDeduction {
  id: number;
  subcontractor_id: number;
  subcontractor_name?: string;
  payment_date: string;
  description?: string;
  invoice_reference?: string;
  gross_amount: number;
  materials_amount: number;
  labour_amount: number;
  deduction_rate: number;
  deduction_amount: number;
  net_payment: number;
  tax_month: number;
  tax_year: string;
  status: string;
}

interface CISReturn {
  id: number;
  tax_month: number;
  tax_year: string;
  period_start: string;
  period_end: string;
  due_date: string;
  total_payments: number;
  total_materials: number;
  total_deductions: number;
  subcontractor_count: number;
  is_nil_return: boolean;
  status: string;
  submission_date?: string;
  hmrc_reference?: string;
}

interface CISDashboard {
  tax_year: string;
  tax_month: number;
  subcontractors: { total: number; verified: number; unverified: number };
  year_to_date: { total_payments: number; total_deductions: number; deduction_count: number };
  returns: { filed: number; total_periods: number; outstanding: number };
  next_return: { tax_month: number; due_date: string; days_until_due: number; is_overdue: boolean };
}

// ============================================================================
// API helpers
// ============================================================================

const cisApi = {
  getDashboard: () => apiClient.get('/cis/dashboard').then(r => r.data),
  getSubcontractors: (activeOnly = true) =>
    apiClient.get('/cis/subcontractors', { params: { active_only: activeOnly } }).then(r => r.data),
  createSubcontractor: (data: any) => apiClient.post('/cis/subcontractors', data).then(r => r.data),
  updateSubcontractor: (id: number, data: any) =>
    apiClient.put(`/cis/subcontractors/${id}`, data).then(r => r.data),
  deleteSubcontractor: (id: number) => apiClient.delete(`/cis/subcontractors/${id}`).then(r => r.data),
  verifySubcontractor: (id: number) => apiClient.post(`/cis/subcontractors/${id}/verify`).then(r => r.data),
  getDeductions: (params?: any) => apiClient.get('/cis/deductions', { params }).then(r => r.data),
  createDeduction: (data: any) => apiClient.post('/cis/deductions', data).then(r => r.data),
  voidDeduction: (id: number) => apiClient.post(`/cis/deductions/${id}/void`).then(r => r.data),
  calculateDeduction: (data: any) => apiClient.post('/cis/deductions/calculate', data).then(r => r.data),
  getReturns: (params?: any) => apiClient.get('/cis/returns', { params }).then(r => r.data),
  generateReturn: (data: any) => apiClient.post('/cis/returns/generate', data).then(r => r.data),
  submitReturn: (id: number) => apiClient.post(`/cis/returns/${id}/submit`).then(r => r.data),
  getStatements: (params?: any) => apiClient.get('/cis/statements', { params }).then(r => r.data),
  generateStatement: (data: any) => apiClient.post('/cis/statements/generate', data).then(r => r.data),
  issueStatement: (id: number, method: string) =>
    apiClient.post(`/cis/statements/${id}/issue`, null, { params: { delivery_method: method } }).then(r => r.data),
};

// ============================================================================
// Formatting helpers
// ============================================================================

const fmt = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-GB') : '—';
const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  const map: Record<string, any> = {
    gross: 'success', net: 'info', higher: 'error', unverified: 'warning',
    draft: 'default', ready: 'info', submitted: 'success', accepted: 'success', rejected: 'error',
    pending: 'warning', applied: 'info', paid: 'success', void: 'error',
  };
  return map[s] || 'default';
};
const taxMonthLabel = (m: number) => {
  const months = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
  return months[(m - 1) % 12] || `M${m}`;
};

// ============================================================================
// Main Component
// ============================================================================

const CISManagement: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [dashboard, setDashboard] = useState<CISDashboard | null>(null);
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [deductions, setDeductions] = useState<CISDeduction[]>([]);
  const [returns, setReturns] = useState<CISReturn[]>([]);

  // Dialogs
  const [showAddSub, setShowAddSub] = useState(false);
  const [showAddDeduction, setShowAddDeduction] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showGenerateReturn, setShowGenerateReturn] = useState(false);

  // Forms
  const [subForm, setSubForm] = useState<any>({});
  const [deductionForm, setDeductionForm] = useState<any>({});
  const [calcResult, setCalcResult] = useState<any>(null);
  const [returnForm, setReturnForm] = useState({ tax_month: 1, tax_year: '2025-26' });

  const loadDashboard = useCallback(async () => {
    try {
      const data = await cisApi.getDashboard();
      setDashboard(data);
    } catch (e) {
      console.error('Dashboard load failed:', e);
    }
  }, []);

  const loadSubcontractors = useCallback(async () => {
    try {
      const data = await cisApi.getSubcontractors();
      setSubcontractors(data.subcontractors || []);
    } catch (e) {
      console.error('Subcontractors load failed:', e);
    }
  }, []);

  const loadDeductions = useCallback(async () => {
    try {
      const data = await cisApi.getDeductions();
      setDeductions(data.deductions || []);
    } catch (e) {
      console.error('Deductions load failed:', e);
    }
  }, []);

  const loadReturns = useCallback(async () => {
    try {
      const data = await cisApi.getReturns();
      setReturns(data.returns || []);
    } catch (e) {
      console.error('Returns load failed:', e);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    loadSubcontractors();
    loadDeductions();
    loadReturns();
  }, [loadDashboard, loadSubcontractors, loadDeductions, loadReturns]);

  // Handlers
  const handleAddSubcontractor = async () => {
    try {
      setLoading(true);
      setError(null);
      await cisApi.createSubcontractor(subForm);
      setShowAddSub(false);
      setSubForm({});
      await loadSubcontractors();
      await loadDashboard();
    } catch (e: any) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (id: number) => {
    try {
      setLoading(true);
      await cisApi.verifySubcontractor(id);
      await loadSubcontractors();
      await loadDashboard();
    } catch (e: any) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeduction = async () => {
    try {
      setLoading(true);
      setError(null);
      await cisApi.createDeduction(deductionForm);
      setShowAddDeduction(false);
      setDeductionForm({});
      await loadDeductions();
      await loadDashboard();
    } catch (e: any) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    try {
      const result = await cisApi.calculateDeduction({
        gross_amount: parseFloat(deductionForm.gross_amount || 0),
        materials_amount: parseFloat(deductionForm.materials_amount || 0),
        verification_status: deductionForm.verification_status || 'net',
      });
      setCalcResult(result);
      setShowCalculator(true);
    } catch (e: any) {
      setError(getErrorMessage(e));
    }
  };

  const handleGenerateReturn = async () => {
    try {
      setLoading(true);
      setError(null);
      await cisApi.generateReturn(returnForm);
      setShowGenerateReturn(false);
      await loadReturns();
      await loadDashboard();
    } catch (e: any) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReturn = async (id: number) => {
    try {
      setLoading(true);
      await cisApi.submitReturn(id);
      await loadReturns();
      await loadDashboard();
    } catch (e: any) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleVoidDeduction = async (id: number) => {
    try {
      setLoading(true);
      await cisApi.voidDeduction(id);
      await loadDeductions();
      await loadDashboard();
    } catch (e: any) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // Tab 0: Dashboard
  // ============================================================================
  const renderDashboard = () => (
    <Grid container spacing={3}>
      {/* KPI Cards */}
      <Grid item xs={12} md={3}>
        <Card sx={{ bgcolor: '#e3f2fd' }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Active Subcontractors</Typography>
            <Typography variant="h3">{dashboard?.subcontractors.total || 0}</Typography>
            <Typography variant="body2" color="text.secondary">
              {dashboard?.subcontractors.verified || 0} verified · {dashboard?.subcontractors.unverified || 0} pending
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card sx={{ bgcolor: '#e8f5e9' }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">YTD Payments</Typography>
            <Typography variant="h4">{fmt(dashboard?.year_to_date.total_payments || 0)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {dashboard?.year_to_date.deduction_count || 0} deductions
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card sx={{ bgcolor: '#fff3e0' }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">YTD Deductions</Typography>
            <Typography variant="h4">{fmt(dashboard?.year_to_date.total_deductions || 0)}</Typography>
            <Typography variant="body2" color="text.secondary">
              Withheld for HMRC
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={3}>
        <Card sx={{
          bgcolor: dashboard?.next_return?.is_overdue ? '#ffebee' : '#f3e5f5',
          border: dashboard?.next_return?.is_overdue ? '2px solid #f44336' : 'none',
        }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">Next CIS300 Due</Typography>
            <Typography variant="h5">{fmtDate(dashboard?.next_return?.due_date)}</Typography>
            <Typography variant="body2" color={dashboard?.next_return?.is_overdue ? 'error' : 'text.secondary'}>
              {dashboard?.next_return?.is_overdue
                ? `⚠️ OVERDUE by ${Math.abs(dashboard?.next_return?.days_until_due || 0)} days`
                : `${dashboard?.next_return?.days_until_due || 0} days remaining`}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Returns Filed Progress */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>CIS300 Returns — {dashboard?.tax_year}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={dashboard?.returns.total_periods
                    ? (dashboard.returns.filed / dashboard.returns.total_periods) * 100
                    : 0}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Typography variant="body2">
                {dashboard?.returns.filed || 0} / {dashboard?.returns.total_periods || 0} filed
              </Typography>
            </Box>
            {(dashboard?.returns.outstanding || 0) > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {dashboard?.returns.outstanding} outstanding return(s) need filing
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Quick Actions */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
            <Stack spacing={1}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setTab(1); setShowAddSub(true); }}>
                Add Subcontractor
              </Button>
              <Button variant="outlined" startIcon={<DeductionIcon />} onClick={() => { setTab(2); setShowAddDeduction(true); }}>
                Record Deduction
              </Button>
              <Button variant="outlined" startIcon={<ReturnIcon />} onClick={() => { setTab(3); setShowGenerateReturn(true); }}>
                Generate CIS300 Return
              </Button>
              <Button variant="outlined" startIcon={<CalcIcon />} onClick={() => setShowCalculator(true)}>
                Deduction Calculator
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* CIS Info */}
      <Grid item xs={12}>
        <Alert severity="info" icon={<InfoIcon />}>
          <strong>Construction Industry Scheme (CIS)</strong> — As a contractor, you must deduct money from
          subcontractor payments and pass it to HMRC. The deduction counts as advance payment of the
          subcontractor's tax and National Insurance. Rates: 20% (verified), 30% (unverified), 0% (gross status).
          Monthly CIS300 returns must be filed by the 19th of each month.
        </Alert>
      </Grid>
    </Grid>
  );

  // ============================================================================
  // Tab 1: Subcontractors
  // ============================================================================
  const renderSubcontractors = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Subcontractor Registry</Typography>
        <Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddSub(true)} sx={{ mr: 1 }}>
            Add Subcontractor
          </Button>
          <IconButton onClick={loadSubcontractors}><RefreshIcon /></IconButton>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Business Name</TableCell>
              <TableCell>UTR</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Deduction Rate</TableCell>
              <TableCell>Verified</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subcontractors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No subcontractors registered yet. Click "Add Subcontractor" to get started.</Typography>
                </TableCell>
              </TableRow>
            ) : subcontractors.map(sub => (
              <TableRow key={sub.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{sub.business_name}</Typography>
                  {sub.trading_name && <Typography variant="body2" color="text.secondary">t/a {sub.trading_name}</Typography>}
                </TableCell>
                <TableCell><code>{sub.utr}</code></TableCell>
                <TableCell>
                  {sub.contact_name && <Typography variant="body2">{sub.contact_name}</Typography>}
                  {sub.email && <Typography variant="body2" color="text.secondary">{sub.email}</Typography>}
                </TableCell>
                <TableCell>
                  <Chip label={sub.verification_status} color={statusColor(sub.verification_status)} size="small" />
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>{sub.deduction_rate}%</Typography>
                </TableCell>
                <TableCell>{fmtDate(sub.verification_date)}</TableCell>
                <TableCell>
                  <Tooltip title="Verify with HMRC">
                    <IconButton size="small" color="primary" onClick={() => handleVerify(sub.id)}>
                      <VerifyIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // ============================================================================
  // Tab 2: Deductions
  // ============================================================================
  const renderDeductions = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">CIS Deductions</Typography>
        <Box>
          <Button variant="outlined" startIcon={<CalcIcon />} onClick={() => setShowCalculator(true)} sx={{ mr: 1 }}>
            Calculator
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddDeduction(true)} sx={{ mr: 1 }}>
            Record Deduction
          </Button>
          <IconButton onClick={loadDeductions}><RefreshIcon /></IconButton>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Date</TableCell>
              <TableCell>Subcontractor</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Gross</TableCell>
              <TableCell align="right">Materials</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Deduction</TableCell>
              <TableCell align="right">Net Payment</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deductions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No deductions recorded yet.</Typography>
                </TableCell>
              </TableRow>
            ) : deductions.map(d => (
              <TableRow key={d.id} hover>
                <TableCell>{fmtDate(d.payment_date)}</TableCell>
                <TableCell>{d.subcontractor_name}</TableCell>
                <TableCell>{d.description || d.invoice_reference || '—'}</TableCell>
                <TableCell align="right">{fmt(d.gross_amount)}</TableCell>
                <TableCell align="right">{fmt(d.materials_amount)}</TableCell>
                <TableCell align="right">{d.deduction_rate}%</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>
                  {fmt(d.deduction_amount)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(d.net_payment)}</TableCell>
                <TableCell><Chip label={d.status} color={statusColor(d.status)} size="small" /></TableCell>
                <TableCell>
                  {d.status === 'pending' && (
                    <Button size="small" color="error" onClick={() => handleVoidDeduction(d.id)}>Void</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // ============================================================================
  // Tab 3: Returns & Statements
  // ============================================================================
  const renderReturns = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">CIS300 Monthly Returns</Typography>
        <Box>
          <Button variant="contained" startIcon={<ReturnIcon />} onClick={() => setShowGenerateReturn(true)} sx={{ mr: 1 }}>
            Generate Return
          </Button>
          <IconButton onClick={loadReturns}><RefreshIcon /></IconButton>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Period</TableCell>
              <TableCell>Tax Year</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="right">Total Payments</TableCell>
              <TableCell align="right">Total Deductions</TableCell>
              <TableCell align="center">Subcontractors</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>HMRC Ref</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {returns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No CIS300 returns yet. Generate one to get started.</Typography>
                </TableCell>
              </TableRow>
            ) : returns.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>Month {r.tax_month} ({taxMonthLabel(r.tax_month)})</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fmtDate(r.period_start)} – {fmtDate(r.period_end)}
                  </Typography>
                </TableCell>
                <TableCell>{r.tax_year}</TableCell>
                <TableCell>
                  <Typography color={new Date(r.due_date) < new Date() && r.status === 'draft' ? 'error' : 'inherit'}>
                    {fmtDate(r.due_date)}
                  </Typography>
                </TableCell>
                <TableCell align="right">{fmt(r.total_payments)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.total_deductions)}</TableCell>
                <TableCell align="center">{r.subcontractor_count}</TableCell>
                <TableCell><Chip label={r.status} color={statusColor(r.status)} size="small" /></TableCell>
                <TableCell><code>{r.hmrc_reference || '—'}</code></TableCell>
                <TableCell>
                  {(r.status === 'ready' || r.status === 'draft') && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<SubmitIcon />}
                      onClick={() => handleSubmitReturn(r.id)}
                    >
                      Submit
                    </Button>
                  )}
                  {r.status === 'submitted' && <Chip label="Filed" color="success" size="small" icon={<CheckIcon />} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // ============================================================================
  // Dialogs
  // ============================================================================

  const addSubcontractorDialog = (
    <Dialog open={showAddSub} onClose={() => setShowAddSub(false)} maxWidth="md" fullWidth>
      <DialogTitle>Add Subcontractor</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Business Name *" value={subForm.business_name || ''}
              onChange={e => setSubForm({ ...subForm, business_name: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Trading Name" value={subForm.trading_name || ''}
              onChange={e => setSubForm({ ...subForm, trading_name: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="UTR (10 digits) *" value={subForm.utr || ''}
              onChange={e => setSubForm({ ...subForm, utr: e.target.value })}
              inputProps={{ maxLength: 10 }} helperText="Unique Taxpayer Reference" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="NI Number" value={subForm.ni_number || ''}
              onChange={e => setSubForm({ ...subForm, ni_number: e.target.value })}
              helperText="For sole traders" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Company Number" value={subForm.company_registration || ''}
              onChange={e => setSubForm({ ...subForm, company_registration: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Contact Name" value={subForm.contact_name || ''}
              onChange={e => setSubForm({ ...subForm, contact_name: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Email" type="email" value={subForm.email || ''}
              onChange={e => setSubForm({ ...subForm, email: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Phone" value={subForm.phone || ''}
              onChange={e => setSubForm({ ...subForm, phone: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Address Line 1" value={subForm.address_line_1 || ''}
              onChange={e => setSubForm({ ...subForm, address_line_1: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Town / City" value={subForm.town_city || ''}
              onChange={e => setSubForm({ ...subForm, town_city: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Postcode" value={subForm.postcode || ''}
              onChange={e => setSubForm({ ...subForm, postcode: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">Business Type</Typography>
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel control={<Checkbox checked={subForm.is_sole_trader || false}
              onChange={e => setSubForm({ ...subForm, is_sole_trader: e.target.checked })} />}
              label="Sole Trader" />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel control={<Checkbox checked={subForm.is_partnership || false}
              onChange={e => setSubForm({ ...subForm, is_partnership: e.target.checked })} />}
              label="Partnership" />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel control={<Checkbox checked={subForm.is_company || false}
              onChange={e => setSubForm({ ...subForm, is_company: e.target.checked })} />}
              label="Limited Company" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowAddSub(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleAddSubcontractor}
          disabled={!subForm.business_name || !subForm.utr || subForm.utr?.length !== 10}>
          Add Subcontractor
        </Button>
      </DialogActions>
    </Dialog>
  );

  const addDeductionDialog = (
    <Dialog open={showAddDeduction} onClose={() => setShowAddDeduction(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Record CIS Deduction</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Subcontractor *</InputLabel>
              <Select value={deductionForm.subcontractor_id || ''} label="Subcontractor *"
                onChange={e => setDeductionForm({ ...deductionForm, subcontractor_id: e.target.value })}>
                {subcontractors.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.business_name} — {s.verification_status} ({s.deduction_rate}%)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Payment Date *" type="date" value={deductionForm.payment_date || ''}
              onChange={e => setDeductionForm({ ...deductionForm, payment_date: e.target.value })}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Invoice Reference" value={deductionForm.invoice_reference || ''}
              onChange={e => setDeductionForm({ ...deductionForm, invoice_reference: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Gross Amount *" type="number" value={deductionForm.gross_amount || ''}
              onChange={e => setDeductionForm({ ...deductionForm, gross_amount: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Materials (CIS-exempt)" type="number" value={deductionForm.materials_amount || ''}
              onChange={e => setDeductionForm({ ...deductionForm, materials_amount: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
              helperText="Materials cost not subject to CIS" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" value={deductionForm.description || ''}
              onChange={e => setDeductionForm({ ...deductionForm, description: e.target.value })} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowAddDeduction(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleAddDeduction}
          disabled={!deductionForm.subcontractor_id || !deductionForm.payment_date || !deductionForm.gross_amount}>
          Record Deduction
        </Button>
      </DialogActions>
    </Dialog>
  );

  const calculatorDialog = (
    <Dialog open={showCalculator} onClose={() => { setShowCalculator(false); setCalcResult(null); }} maxWidth="sm" fullWidth>
      <DialogTitle>CIS Deduction Calculator</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Gross Amount" type="number"
              value={deductionForm.gross_amount || ''}
              onChange={e => setDeductionForm({ ...deductionForm, gross_amount: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Materials" type="number"
              value={deductionForm.materials_amount || ''}
              onChange={e => setDeductionForm({ ...deductionForm, materials_amount: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }} />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Verification Status</InputLabel>
              <Select value={deductionForm.verification_status || 'net'} label="Verification Status"
                onChange={e => setDeductionForm({ ...deductionForm, verification_status: e.target.value })}>
                <MenuItem value="gross">Gross (0%)</MenuItem>
                <MenuItem value="net">Net / Verified (20%)</MenuItem>
                <MenuItem value="higher">Higher / Unverified (30%)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button fullWidth variant="outlined" startIcon={<CalcIcon />} onClick={handleCalculate}>
              Calculate
            </Button>
          </Grid>
          {calcResult && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>Result</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}><Typography color="text.secondary">Gross Amount:</Typography></Grid>
                  <Grid item xs={6}><Typography align="right">{fmt(calcResult.gross_amount)}</Typography></Grid>
                  <Grid item xs={6}><Typography color="text.secondary">Materials:</Typography></Grid>
                  <Grid item xs={6}><Typography align="right">{fmt(calcResult.materials_amount)}</Typography></Grid>
                  <Grid item xs={6}><Typography color="text.secondary">Labour (CIS-liable):</Typography></Grid>
                  <Grid item xs={6}><Typography align="right">{fmt(calcResult.labour_amount)}</Typography></Grid>
                  <Grid item xs={12}><Divider /></Grid>
                  <Grid item xs={6}><Typography color="text.secondary">Deduction Rate:</Typography></Grid>
                  <Grid item xs={6}><Typography align="right" fontWeight={600}>{calcResult.deduction_rate}%</Typography></Grid>
                  <Grid item xs={6}><Typography color="error">CIS Deduction:</Typography></Grid>
                  <Grid item xs={6}><Typography align="right" fontWeight={600} color="error">{fmt(calcResult.deduction_amount)}</Typography></Grid>
                  <Grid item xs={12}><Divider /></Grid>
                  <Grid item xs={6}><Typography fontWeight={600}>Net Payment:</Typography></Grid>
                  <Grid item xs={6}><Typography align="right" fontWeight={600} color="success.main">{fmt(calcResult.net_payment)}</Typography></Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setShowCalculator(false); setCalcResult(null); }}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  const generateReturnDialog = (
    <Dialog open={showGenerateReturn} onClose={() => setShowGenerateReturn(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Generate CIS300 Return</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This will aggregate all CIS deductions for the selected period into a CIS300 monthly return.
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Tax Month</InputLabel>
              <Select value={returnForm.tax_month} label="Tax Month"
                onChange={e => setReturnForm({ ...returnForm, tax_month: Number(e.target.value) })}>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                  <MenuItem key={m} value={m}>Month {m} ({taxMonthLabel(m)})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Tax Year" value={returnForm.tax_year}
              onChange={e => setReturnForm({ ...returnForm, tax_year: e.target.value })}
              helperText="e.g. 2025-26" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowGenerateReturn(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleGenerateReturn}>Generate Return</Button>
      </DialogActions>
    </Dialog>
  );

  // ============================================================================
  // Main Render
  // ============================================================================
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <CISIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">Construction Industry Scheme (CIS)</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage subcontractors, deductions, CIS300 returns &amp; payment statements
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab icon={<DashboardIcon />} label="Dashboard" />
          <Tab icon={<SubIcon />} label="Subcontractors" />
          <Tab icon={<DeductionIcon />} label="Deductions" />
          <Tab icon={<ReturnIcon />} label="Returns & Statements" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tab === 0 && renderDashboard()}
      {tab === 1 && renderSubcontractors()}
      {tab === 2 && renderDeductions()}
      {tab === 3 && renderReturns()}

      {/* Dialogs */}
      {addSubcontractorDialog}
      {addDeductionDialog}
      {calculatorDialog}
      {generateReturnDialog}
    </Container>
  );
};

export default CISManagement;
